/**
 * Fiscal Calculator for SAFECARE Act
 * Calculates Trust Fund balance, reserve ratios, and federal debt impacts
 */

import { SAFECARE_BILL_PARAMETERS } from './billParameters.js';
import { projectSpending, getGrowthRateBreakdown } from '../calculators/growthRateCalculator.js';
import { compoundingEffectsConfig } from '../config/compoundingEffects.js';

/**
 * Get General Fund transfer limit based on years active (Sec 207(c))
 */
function getGeneralFundLimit(yearsActive) {
    const limits = SAFECARE_BILL_PARAMETERS.trustFund.generalFundLimits;

    for (const limit of limits) {
        if (yearsActive >= limit.years[0] && (yearsActive <= limit.years[1] || limit.years[1] === Infinity)) {
            return limit.maxPercent;
        }
    }

    return 0; // Default to 0 if not found
}

/**
 * Calculate Trust Fund balance and reserve ratio for a given year
 * ENHANCED: Now calculates detailed compounding savings breakdown
 */
export function calculateTrustFundBalance(year, revenues, spending, priorBalance, config, baselineData, accumulatedGeneralFundDebt = 0, priorTrustData = []) {
    const yearRevenue = revenues.find(r => r.year === year);
    const yearSpending = spending.find(s => s.year === year);


    if (!yearRevenue || !yearSpending) {
        console.warn(`[FiscalCalc] Missing data for year ${year}`);
        return null;
    }

    // Calculate balance
    const inflows = yearRevenue.totalRevenue;
    const outflows = yearSpending.totalProgramCost || yearSpending.safecareTotal; // Support both V2 and V1 keys
    const netChange = inflows - outflows;
    const endingBalance = priorBalance + netChange;

    // Calculate reserve ratio (ending balance / next year's projected outlays)
    const nextYearSpending = spending.find(s => s.year === year + 1);
    const reserveRatio = nextYearSpending ?
        endingBalance / (nextYearSpending.totalProgramCost || nextYearSpending.safecareTotal) :
        endingBalance / outflows;

    // Determine if automatic rate adjustment is needed (Section 206)
    let rateAdjustmentNeeded = 'none';
    if (config.enforceTrustFundRules > 0) {
        if (reserveRatio < config.reserveTargetMin) {
            rateAdjustmentNeeded = 'increase';
        } else if (reserveRatio > config.reserveTargetMax) {
            rateAdjustmentNeeded = 'decrease';
        }
    }

    // Calculate General Fund transfer requirement (Sec 207)
    const yearsActive = year - config.startYear + 1; // 1-based indexing for bill parameters (Year 1 = 2027)
    const generalFundLimit = getGeneralFundLimit(yearsActive);
    const maxGeneralFundTransfer = outflows * generalFundLimit;

    // Actual General Fund transfer needed (if revenues fall short)
    // Note: This logic assumes transfer fills the gap to avoid negative balance or cash flow
    const revenueShortfall = Math.max(0, outflows - inflows);
    const actualGeneralFundTransfer = Math.min(revenueShortfall, maxGeneralFundTransfer);

    // Adjusted balance (including General Fund transfers)
    let adjustedNetChange = netChange + actualGeneralFundTransfer;
    let adjustedEndingBalance = priorBalance + adjustedNetChange;

    // Repayment Logic (Section 207(e) compliance / User Request)
    // Constraint 1: Use surplus (positive net change)
    // Constraint 2: Do not deplete reserves below target minimum (5%)
    let generalFundRepayment = 0;

    // We can only repay if we have a positive balance and positive net change
    const operatingSurplus = Math.max(0, adjustedNetChange);

    if (operatingSurplus > 0 && accumulatedGeneralFundDebt > 0) {
        // Calculate Reserve Floor (Safe Harbor)
        // Use next year's spending for robust target, or current outflows if projected not available
        const reserveBasis = nextYearSpending ?
            (nextYearSpending.totalProgramCost || nextYearSpending.safecareTotal) :
            outflows;

        const minReserves = reserveBasis * config.reserveTargetMin;

        // Max we can pay while keeping balance >= minReserves
        // Current Balance = adjustedEndingBalance
        // Post-Repayment Balance = adjustedEndingBalance - Repayment
        // So: adjustedEndingBalance - Repayment >= minReserves
        // Repayment <= adjustedEndingBalance - minReserves
        const maxRepaymentByReserves = Math.max(0, adjustedEndingBalance - minReserves);

        // We limit repayment to the SMALLER of:
        // 1. The Debt itself
        // 2. The Operating Surplus (don't burn prior reserves, just new money)
        // 3. The Reserve Safety Cap (don't drop below 5%)
        generalFundRepayment = Math.min(
            accumulatedGeneralFundDebt,
            operatingSurplus,
            maxRepaymentByReserves
        );

        // Apply repayment
        adjustedNetChange -= generalFundRepayment;
        adjustedEndingBalance -= generalFundRepayment;
    }

    const verificationDebt = accumulatedGeneralFundDebt + actualGeneralFundTransfer - generalFundRepayment;

    // ===========================================================================
    // NEW: Track compounding effects contribution to surplus
    // ===========================================================================

    // Calculate what spending WOULD have been without compounding effects
    // We project from the start year baseline using standard rates
    // spending[0] is the anchor
    const baseYearSpendingRaw = spending[0].totalProgramCost || spending[0].safecareTotal;
    const hypotheticalSpendingNoCompounding = projectSpending(
        config.startYear,
        year,
        baseYearSpendingRaw,
        'overall',
        false // Use baseline growth rates (no SAFECARE effects)
    );

    const actualSpending = outflows;
    const savingsFromCompounding = Math.max(0, hypotheticalSpendingNoCompounding - actualSpending);

    // Detailed component breakdown
    const componentSavings = calculateComponentSavings(year, yearSpending, config);
    const growthBreakdown = getGrowthRateBreakdown(year);

    // Solvency status
    const monthsOfReserves = (adjustedEndingBalance / outflows) * 12;
    const solvencyStatus = getSolvencyStatus(monthsOfReserves, adjustedEndingBalance);

    // Sustainability Score
    const sustainabilityScore = calculateSustainabilityScore(
        adjustedNetChange,
        outflows,
        growthBreakdown,
        yearsActive
    );

    return {
        year,
        inflows,
        outflows,
        netChange,
        endingBalance,
        redirectedSpending: yearRevenue.redirectedFederalSpending,

        // General Fund tracking
        generalFundLimit,
        maxGeneralFundTransfer,
        actualGeneralFundTransfer, // Borrowing
        generalFundRepayment,      // Repayment
        accumulatedGeneralFundDebt: verificationDebt, // Ending Debt Balance
        adjustedNetChange,
        adjustedEndingBalance,

        reserveRatio,
        rateAdjustmentNeeded,
        isHealthy: reserveRatio >= config.reserveTargetMin && reserveRatio <= config.reserveTargetMax,

        // NEW METRICS
        savingsFromCompounding,
        savingsFromCompoundingPercent: (savingsFromCompounding / hypotheticalSpendingNoCompounding) * 100,
        componentSavings,
        growthRates: {
            baseline: growthBreakdown.baseline.overall,
            safecare: growthBreakdown.safecare.overall,
            reduction: growthBreakdown.reductionEffect.total,
        },
        monthsOfReserves,
        solvencyStatus,
        sustainabilityScore,
        hypotheticalWithoutCompounding: {
            spending: hypotheticalSpendingNoCompounding,
            // Hypothetical balance is rough approximation
            cumulativeBalance: adjustedEndingBalance - savingsFromCompounding,
            wouldBeInsolvent: (adjustedEndingBalance - savingsFromCompounding) < 0
        },
        // Preserve Rate Data from Simulation Loop
        ratesApplied: priorTrustData.find(t => t.year === year)?.ratesApplied
    };
}

/**
 * Calculate Trust Fund projection for all years
 * Returns { yearlyData: [], summary: {}, finalBalance: number }
 */
export function calculateTrustFundProjection(baselineData, revenues, spending, config) {
    const yearlyData = [];
    let balance = config.initialTrustFundSeed || 0; // Configurable seed
    let accumulatedGeneralFundDebt = 0; // NEW: Track debt across years

    baselineData.nheBaseline.forEach(yearData => {
        const yearResult = calculateTrustFundBalance(
            yearData.year,
            revenues,
            spending,
            balance,
            config,
            baselineData,
            accumulatedGeneralFundDebt, // Pass current debt
            config.trustFundInput // HACK: Pass original trustFund array via config to avoid changing signature too much
        );

        if (yearResult) {
            yearlyData.push(yearResult);
            balance = yearResult.adjustedEndingBalance; // Carry forward adjusted balance
            accumulatedGeneralFundDebt = yearResult.accumulatedGeneralFundDebt; // Carry forward debt
        }
    });

    const summary = generateTrustFundSummary(yearlyData, yearlyData.map(y => y.year), config);

    return {
        yearlyData,
        summary,
        finalBalance: balance,
        isSolvent: balance > 0
    };
}

// ===========================================================================
// NEW HELPER FUNCTIONS
// ===========================================================================

function calculateComponentSavings(year, spendingObj, config) {
    const yearsActive = year - config.startYear + 1; // 1-based indexing

    // Extract spending components - adapting to object structure in spendingCalculatorV2
    // spendingCalculatorV2 returns { physician: ..., drug: ..., provider: ... }
    // Note: providerSpendingAfterMalpractice is 'provider'

    const physicianSpending = spendingObj.physician || 0;
    const providerSpending = spendingObj.provider || 0;
    const drugSpending = spendingObj.drug || 0;
    const adminSpending = spendingObj.admin || 0;
    const totalSpending = spendingObj.totalProgramCost || spendingObj.safecareTotal || 0;

    return {
        netVsGrossPay: {
            savings: calculateNetVsGrossPaySavings(physicianSpending, yearsActive),
            mechanism: "Lower wage growth due to eliminated household healthcare costs"
        },
        medicalEducation: {
            savings: calculateMedicalEducationSavings(physicianSpending, yearsActive),
            workforceDebtFreePercent: Math.min(yearsActive * 5, 100),
            mechanism: "Debt-free workforce accepts lower salaries"
        },
        adminTime: {
            savings: calculateAdminTimeSavings(providerSpending, yearsActive),
            productivityGain: yearsActive >= 2 ? 42 : 0,
            mechanism: "Physicians spend more time on patient care, less on billing"
        },
        drugRDEfficiency: {
            savings: calculateDrugRDSavings(drugSpending, yearsActive),
            innovationMultiplier: yearsActive >= 5 ? 1.75 : 1.0,
            mechanism: "Eliminate wasteful me-too drugs, focus on real innovation"
        },
        capitalFinancing: {
            savings: calculateCapitalFinancingSavings(providerSpending, yearsActive),
            legacyDebtRemaining: Math.max(100 - (yearsActive / 30) * 100, 0),
            mechanism: "Direct capital grants eliminate bond interest"
        },
        preventiveCare: {
            savings: calculatePreventiveCareSavings(totalSpending, yearsActive),
            complicationReduction: yearsActive >= 5 ? ((yearsActive - 5) * 3) : 0,
            mechanism: "Early screening prevents expensive complications"
        },
        adminCascade: {
            savings: calculateAdminCascadeSavings(adminSpending, yearsActive),
            currentAdminRate: getAdminOverheadRate(yearsActive),
            targetAdminRate: 0.02,
            mechanism: "Single billing system eliminates arms race complexity"
        }
    };
}

function calculateNetVsGrossPaySavings(physicianSpending, yearsActive) {
    if (yearsActive <= 1) return 0;
    const effectRate = yearsActive <= 5 ? 0.005 : 0.008;
    return physicianSpending * effectRate * yearsActive * 0.5;
}

function calculateMedicalEducationSavings(physicianSpending, yearsActive) {
    const debtFreePercent = Math.min(yearsActive * 0.05, 1.0);
    return physicianSpending * 0.10 * debtFreePercent;
}

function calculateAdminTimeSavings(providerSpending, yearsActive) {
    if (yearsActive <= 1) return 0;
    const productivityGain = 0.42;
    const phasedGain = Math.min(yearsActive / 5, 1.0) * productivityGain;
    return providerSpending * phasedGain * 0.3;
}

function calculateDrugRDSavings(drugSpending, yearsActive) {
    if (yearsActive <= 5) return drugSpending * 0.001 * yearsActive;
    return drugSpending * 0.011 * (yearsActive - 5);
}

function calculateCapitalFinancingSavings(providerSpending, yearsActive) {
    const capitalSpending = providerSpending * 0.10;
    const interestCosts = capitalSpending * 0.35;
    const phaseOutPercent = Math.min(yearsActive / 30, 1.0);
    return interestCosts * phaseOutPercent;
}

function calculatePreventiveCareSavings(totalSpending, yearsActive) {
    if (yearsActive <= 5) return 0;
    const yearsOfEffect = yearsActive - 5;
    const maxEffect = 0.004;
    const currentEffect = Math.min(yearsOfEffect / 15, 1.0) * maxEffect;
    return totalSpending * currentEffect;
}

function calculateAdminCascadeSavings(adminSpending, yearsActive) {
    // adminSpending is the ACTUAL spending.
    // We want to know how much IS being saved compared to baseline rate.
    const actualRate = getAdminOverheadRate(yearsActive);
    const baselineRate = 0.22;
    // impliedBaseline = actual / actualRate * baselineRate
    // savings = impliedBaseline - actual
    // Safety check for div by 0
    if (actualRate < 0.001) return 0;

    const impliedBaseline = (adminSpending / actualRate) * baselineRate;
    return Math.max(0, impliedBaseline - adminSpending);
}

function getAdminOverheadRate(yearIndex) {
    if (yearIndex <= 0) return 0.22;
    if (yearIndex >= 10) return 0.02;
    const decayRate = Math.log(0.02 / 0.22) / 10;
    return 0.22 * Math.exp(decayRate * yearIndex);
}

function getSolvencyStatus(monthsOfReserves, balance) {
    if (balance < 0) return { status: 'INSOLVENT', color: 'red', icon: '⚠️' };
    if (monthsOfReserves < 3) return { status: 'CRITICAL', color: 'orange', icon: '⚡' };
    if (monthsOfReserves < 6) return { status: 'STRESSED', color: 'yellow', icon: '⚠' };
    if (monthsOfReserves < 12) return { status: 'ADEQUATE', color: 'lightgreen', icon: '✓' };
    return { status: 'HEALTHY', color: 'green', icon: '✓✓' };
}

function calculateSustainabilityScore(annualBalance, spending, growthBreakdown, yearIndex) {
    if (!spending) return 0;
    let score = 50;
    const surplusRatio = annualBalance / spending;
    score += surplusRatio * 30;

    const growthReduction = growthBreakdown.reductionEffect.total;
    score += Math.min(growthReduction / 0.04, 1.0) * 20;

    const timeBonus = Math.min(yearIndex / 10, 1.0) * 20;
    score += timeBonus;

    return Math.max(0, Math.min(100, score));
}

function generateTrustFundSummary(trustFundBalance, years, config) {
    const firstYear = trustFundBalance[0];
    const lastYear = trustFundBalance[trustFundBalance.length - 1];
    const midpoint = trustFundBalance[Math.floor(trustFundBalance.length / 2)];

    const totalSavingsFromCompounding = trustFundBalance.reduce(
        (sum, year) => sum + year.savingsFromCompounding,
        0
    );

    const componentContributions = {
        netVsGrossPay: 0,
        medicalEducation: 0,
        adminTime: 0,
        drugRDEfficiency: 0,
        capitalFinancing: 0,
        preventiveCare: 0,
        adminCascade: 0,
    };

    trustFundBalance.forEach(year => {
        Object.keys(componentContributions).forEach(component => {
            if (year.componentSavings && year.componentSavings[component]) {
                componentContributions[component] += year.componentSavings[component].savings || 0;
            }
        });
    });

    // Identify largest
    let largestContributor = 'None';
    let maxVal = 0;
    Object.keys(componentContributions).forEach(k => {
        if (componentContributions[k] > maxVal) {
            maxVal = componentContributions[k];
            largestContributor = k;
        }
    });

    return {
        timeHorizon: {
            startYear: years[0],
            endYear: years[years.length - 1],
            totalYears: years.length,
        },
        financialSummary: {
            totalRevenue: trustFundBalance.reduce((sum, y) => sum + y.inflows, 0),
            totalSpending: trustFundBalance.reduce((sum, y) => sum + y.outflows, 0),
            totalSurplus: trustFundBalance.reduce((sum, y) => sum + y.adjustedNetChange, 0),
            finalBalance: lastYear.adjustedEndingBalance,
            isSolvent: lastYear.adjustedEndingBalance > 0,
        },
        compoundingImpact: {
            totalSavings: totalSavingsFromCompounding,
            percentOfSpending: (totalSavingsFromCompounding / trustFundBalance.reduce((sum, y) => sum + y.outflows, 0)) * 100,
            averageAnnualSavings: totalSavingsFromCompounding / trustFundBalance.length,
            componentContributions,
            largestContributor,
        },
        growthRateEvolution: {
            year1: firstYear.growthRates,
            year10: midpoint?.growthRates || firstYear.growthRates,
            year20: lastYear.growthRates,
            totalReduction: lastYear.growthRates.reduction,
        },
        solvencyMetrics: {
            worstCaseBalance: Math.min(...trustFundBalance.map(y => y.adjustedEndingBalance)),
            bestCaseBalance: Math.max(...trustFundBalance.map(y => y.adjustedEndingBalance)),
            averageMonthsReserves: trustFundBalance.reduce((sum, y) => sum + y.monthsOfReserves, 0) / trustFundBalance.length,
        }
    };
}

/**
 * Calculate federal debt impact for a given year
 * V3: Bill-compliant calculation based on Sections 1005, 402, 207
 * 
 * When Medicare/Medicaid enrollees transition to SAFECARE, the federal government:
 * 1. Stops spending on those programs (baseline federal health outlays)
 * 2. SAFECARE covers them at Medicare rates (lower cost)
 * 3. Federal savings = Baseline - SAFECARE cost for federal beneficiaries
 */
export function calculateFederalDebtImpact(year, baselineData, spending, revenues, trustFund, config, gdpMultiplier = 1.0) {
    const baselineFiscal = baselineData.federalDebtBaseline.find(d => d.year === year);
    const yearSpending = spending.find(s => s.year === year);
    const yearRevenue = revenues.find(r => r.year === year);
    const yearTrust = trustFund.find(t => t.year === year);

    if (!baselineFiscal || !yearSpending || !yearTrust || !yearRevenue) return null;

    // 1. Baseline: What federal government would spend on Medicare/Medicaid/CHIP/ACA/VA
    const baselineFederalHealth = baselineFiscal.healthOutlays;

    // 2. Calculate federal beneficiary share (Implied)
    // We use the revenue redirect amount which is based on the statutory 60% share
    // This ensures alignment between Trust Fund inflows and Federal Debt outflows.
    const federalBeneficiaryShare = 0.60;

    // 3. SAFECARE cost for federal beneficiaries
    // FIXED: Use the calculated Federal Redirect amount from Revenue Calculator
    // This represents the "Fair Share" (60%) contribution defined in Title X,
    // rather than the enrollment-skewed ratio which incorrectly assigned ~80%+ of costs to feds during transition.
    const safecareCostForFederalBeneficiaries = yearRevenue.revenueFederalRedirect;

    // 4. Federal liability under SAFECARE
    // = What SAFECARE costs for federal beneficiaries + General Fund Transfer (deficit plug)
    const federalLiability = safecareCostForFederalBeneficiaries + yearTrust.generalFundTransfer;

    // 5. Federal savings (positive = deficit reduction)
    // Baseline federal health - SAFECARE federal liability
    const federalSavings = baselineFederalHealth - federalLiability;

    // Debug logging

    // 6. Calculate deficit impact
    const gdp = baselineData.nheBaseline.find(d => d.year === year).gdp * gdpMultiplier;
    const baselineDeficit = baselineFiscal.deficitToGDP * gdp;

    // SAFECARE Deficit = Baseline Deficit - Federal Savings
    const safecareDeficit = baselineDeficit - federalSavings;

    const safecareDeficitToGDP = safecareDeficit / gdp;
    const deficitReduction = baselineFiscal.deficitToGDP - safecareDeficitToGDP;

    return {
        year,
        baselineFederalHealth,
        // SAFECARE metrics
        federalLiability,
        safecareCostForFederalBeneficiaries, // NEW: Track SAFECARE cost for feds
        federalBeneficiaryShare, // NEW: Track share
        redirectedSpending: yearRevenue.redirectedFederalSpending, // Keep for reference
        generalFundTransfer: yearTrust.generalFundTransfer,
        federalSavings,
        // Deficit metrics
        baselineDebtToGDP: baselineFiscal.debtToGDP,
        baselineDeficitToGDP: baselineFiscal.deficitToGDP,
        safecareDeficitToGDP,
        deficitReduction
    };
}

/**
 * Calculate cumulative federal debt path under SAFECARE
 */
export function calculateDebtProjection(baselineData, spending, revenues, trustFund, config) {
    const projection = [];

    // We need to track the ABSOLUTE stock of debt, not just sum percentages.
    // Initial Debt Stock (Start of Simulation)
    // We assume Safecare Debt starts equal to Baseline Debt in the first year
    // But we need the PRIOR year's debt to iterate relative changes? 
    // Actually, we can just calculate the CUMULATIVE SAVINGS in dollars and subtract from Baseline Debt Stock.
    // SafecareDebt[t] = BaselineDebt[t] - CumulativeSavings[t]

    let cumulativeFederalSavings = 0; // In Dollars

    baselineData.federalDebtBaseline.forEach(fiscalData => {
        const impact = calculateFederalDebtImpact(
            fiscalData.year,
            baselineData,
            spending,
            revenues,
            trustFund,
            config
        );

        if (impact) {
            // Get current year GDP (Safecare adjusted) from the impact calculation inputs or re-derive
            // impact.baselineDeficitToGDP is available.
            // We need absolute GDP to convert savings percentages if needed, but 'federalSavings' is already in dollars in 'impact'.

            // Accumulate absolute savings ($)
            cumulativeFederalSavings += impact.federalSavings;

            // Calculate Baseline Debt Stock ($)
            // We assume baselineData has correct GDP. 
            // Note: calculateFederalDebtImpact uses 'gdpMultiplier', but we need the specific GDP used there.
            // Let's re-fetch the GDP from baselineData and apply multiplier if accessible, 
            // but simpler: SafecareDebtToGDP = (BaselineDebt$ - CumulativeSavings$) / GDP$
            // SafecareDebtToGDP = BaselineDebtToGDP - (CumulativeSavings$ / GDP$)

            // Re-find GDP for this year
            const yearBase = baselineData.nheBaseline.find(d => d.year === fiscalData.year);
            // We don't have the exact dynamic GDP here easily without passing it down.
            // However, we can infer it: impact.federalSavings was calculated using the dynamic GDP?
            // Actually calculateFederalDebtImpact returned 'safecareDeficitToGDP'.
            // Let's rely on the ratio algebra:
            // SafecareDebtToGDP = BaselineDebtToGDP - (CumulativeSavings / GDP)

            // We need the GDP. 
            // If we assume the GDP in baselineData is the denominator (ignoring dynamic growth feedback on the denominator for the ratio calc to keep it comparable to baseline red line),
            // OR if we want to be precise, we use the potentially boosted GDP.
            // Given the chart compares "% of GDP", using the SAME GDP (Baseline) for both lines is the most fair "apples to apples" comparison of the DEBT burden.
            // If Safecare increases GDP, the ratio drops even faster.
            // Let's use Baseline GDP for the denominator to be conservative and consistent with the Red Line.

            const gdp = yearBase ? yearBase.gdp : 0;
            const reductionRatio = gdp > 0 ? (cumulativeFederalSavings / gdp) : 0;

            projection.push({
                ...impact,
                safecareDebtToGDP: impact.baselineDebtToGDP - reductionRatio,
                cumulativeDebtReduction: reductionRatio // Now correctly calculated as (Total Savings / Current GDP)
            });
        }
    });

    return projection;
}
